(() => {
  if (customElements.get('product-description-tabs')) return;

  const icons = {
    description: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2.8h8l4 4V21H6V2.8Zm8 0v4h4M9 11h6M9 15h6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    ingredients: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 3.2C12.4 3.4 6.35 6.25 5.2 11.05c-.52 2.2.3 4.12 1.68 5.24 2.58-3.57 5.65-6.1 9.2-7.63-3.15 2.14-5.62 4.9-7.45 8.3 1.74.7 3.97.45 5.75-.78 4.57-3.14 5.62-8.4 6.42-12.98ZM5.67 16.4c-1.22 1.34-2.02 2.66-2.47 4.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    benefits: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 5.7a5.1 5.1 0 0 0-7.2 0L12 7.3l-1.6-1.6a5.1 5.1 0 0 0-7.2 7.2L12 21l8.8-8.1a5.1 5.1 0 0 0 0-7.2Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    storage: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 7 8-4 8 4-8 4-8-4Zm0 0v10l8 4 8-4V7M12 11v10M8.5 5.25l8 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  class ProductDescriptionTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enhanced === 'true') return;
      this.enhance();
    }

    enhance() {
      const source = this.querySelector('[data-source]');
      const tabs = this.querySelector('[data-tabs]');
      const shell = this.querySelector('[data-tabs-shell]');
      const panels = this.querySelector('[data-panels]');
      if (!source || !tabs || !shell || !panels) return;

      // Only split at top-level H3s. Complex nested markup stays intact in one panel.
      const children = Array.from(source.childNodes);
      const canSplit = Array.from(source.querySelectorAll('h3')).every((heading) => heading.parentElement === source);
      const fallbackTitle = this.dataset.fallbackLabel || 'Description';
      const groups = [];
      let current = { title: fallbackTitle, nodes: [] };
      const hasContent = (nodes) => nodes.some((node) => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim();
        if (!(node instanceof Element)) return false;
        return node.textContent.trim() || node.matches('h3, img, video, audio, iframe, svg, table, hr, picture') || node.querySelector('img, video, audio, iframe, svg, table, hr, picture');
      });
      children.forEach((node) => {
        if (canSplit && node.nodeName === 'H3') {
          if (hasContent(current.nodes)) groups.push(current);
          current = { title: node.textContent.trim() || fallbackTitle, nodes: [] };
          return;
        }
        current.nodes.push(node);
      });
      if (current.nodes.length) groups.push(current);
      if (!groups.length) return;

      groups.forEach((group, index) => {
        const tabId = `DescriptionTab-${this.dataset.sectionId}-${index}`;
        const panelId = `DescriptionPanel-${this.dataset.sectionId}-${index}`;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ppdt__tab';
        button.id = tabId;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panelId);
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        button.tabIndex = index === 0 ? 0 : -1;
        button.dataset.tabIndex = String(index);

        if (this.dataset.showIcons === 'true') {
          const icon = document.createElement('span');
          icon.className = 'ppdt__tab-icon';
          icon.innerHTML = this.iconFor(group.title);
          button.append(icon);
        }
        const label = document.createElement('span');
        label.textContent = group.title;
        button.append(label);
        tabs.append(button);

        const panel = document.createElement('section');
        panel.className = `ppdt__panel${index === 0 ? ' is-active' : ''}`;
        panel.id = panelId;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabId);
        panel.tabIndex = 0;
        panel.hidden = index !== 0;

        const content = document.createElement('div');
        content.className = 'ppdt__content rte';
        // Move the original nodes to preserve IDs, links, media and embedded content.
        content.append(...group.nodes);
        panel.append(content);
        if (groups.length === 1) {
          panel.setAttribute('role', 'region');
          panel.removeAttribute('aria-labelledby');
          panel.setAttribute('aria-label', group.title);
        }
        panels.append(panel);
      });

      tabs.addEventListener('click', (event) => {
        const button = event.target.closest('[role="tab"]');
        if (button) this.activate(Number(button.dataset.tabIndex), true);
      });
      tabs.addEventListener('keydown', (event) => this.handleKeys(event));
      shell.hidden = groups.length < 2;
      this.dataset.enhanced = 'true';
    }

    iconFor(title) {
      const value = title.toLowerCase();
      if (value.includes('ingredient') || value.includes('composition')) return icons.ingredients;
      if (value.includes('benefit') || value.includes('health')) return icons.benefits;
      if (value.includes('storage') || value.includes('store') || value.includes('care')) return icons.storage;
      return icons.description;
    }

    activate(index, focus) {
      const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      const panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active) tab.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest', inline: 'center' });
      });
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      if (focus) tabs[index]?.focus({ preventScroll: true });
    }

    handleKeys(event) {
      const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      const current = tabs.indexOf(document.activeElement);
      if (current < 0) return;
      let next = current;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      else return;
      event.preventDefault();
      this.activate(next, true);
    }
  }

  customElements.define('product-description-tabs', ProductDescriptionTabs);
})();
